// The power network (§31 S6, design "Coal Generator" … "Power Cabling Tower").
//
// Once a tick, before the ovens, each side's sources hand fuel points to the
// refiners that want one (`wantsFuel`), into the same per-machine pool a
// refiner fills by burning its own coal (`IndustryState.fuel`, M2's seam) —
// so a refiner in reach of a generator cooks instead of burning. Then what a
// generator has left charges the batteries in reach.
//
// - **Sources**: a Coal Generator makes up to `POWER.coalRate` a tick, one
//   coal from its stock each; a Nuclear Generator — or the Sacred Well
//   (§31 A4) — `POWER.nuclearRate`, no coal; a Battery gives from its charge, no limit a tick. Generators first,
//   then batteries, each in square order.
// - **Reach**: Chebyshev `POWER.reach` from the source, or from any of its
//   owner's Cabling Towers chained to it (a tower within reach of the source
//   or of a chained tower). A battery's intake chains the same way.
// - **Priority** (design): structures (Shield Generators, then turrets,
//   §31 A9, S8) → upgrades/forging → refiners with no coal → refiners with
//   coal → batteries. An upgrade draws on demand, outside the tick (`drawNetworkFuel`,
//   §31 P3), so a tick is the last three, ties by square.
//
// Deterministic: every sweep is ascending squares, no Map order.

import { type Color, fileOf, pieceColor, rankOf } from "../board";
import { pieceAt, type Position } from "../position";
import { DEFLECTOR, POWER } from "../rules";
import { poweredGearAt } from "./gear";
import {
  BATTERY,
  CABLING_TOWER,
  COAL_GENERATOR,
  type IndustryState,
  NUCLEAR_GENERATOR,
  SHIELD_GENERATOR,
  setFuel,
  type Structure,
  structureAt,
  structureSquares,
  WELL
} from "./state";
import { coalHeld, isCoal, wantsFuel } from "./machines";
import { turretFuelWant } from "./turret";

const inReach = (a: number, b: number): boolean =>
  Math.max(Math.abs(fileOf(a) - fileOf(b)), Math.abs(rankOf(a) - rankOf(b))) <= POWER.reach;

/**
 * The hubs a source at `at` radiates from: itself, then every Cabling Tower
 * of `owner`'s chained to it, in the order they joined (towers ascending).
 */
export function hubsOf(industry: IndustryState, owner: Color, at: number): number[] {
  const towers = structureSquares(industry).filter((sq) => {
    const one = structureAt(industry, sq)!;
    return one.kind === CABLING_TOWER && one.owner === owner;
  });
  const hubs = [at];
  const joined = new Set<number>();
  for (let grew = true; grew; ) {
    grew = false;
    for (const tower of towers) {
      if (joined.has(tower) || !hubs.some((hub) => inReach(hub, tower))) continue;
      joined.add(tower);
      hubs.push(tower);
      grew = true;
    }
  }
  return hubs;
}

/** Does a network radiating from these hubs reach `sq`? */
export const hubsReach = (hubs: readonly number[], sq: number): boolean =>
  hubs.some((hub) => inReach(hub, sq));

/** Coal in a structure's stock. */
const coalIn = (structure: Structure): number => structure.stock.filter(isCoal).length;

interface Source {
  at: number;
  structure: Structure;
  /** Points it may still give this tick. */
  left: number;
  hubs: number[];
}

/** Take one point from a source: a coal burned, a charge spent, or a nuclear point. */
function spend(source: Source): void {
  source.left--;
  const { structure } = source;
  if (structure.kind === COAL_GENERATOR) {
    const index = structure.stock.findIndex(isCoal);
    if (index >= 0) structure.stock.splice(index, 1);
  } else if (structure.kind === BATTERY) {
    structure.charge = Math.max(0, (structure.charge ?? 0) - 1);
  }
}

export interface PowerReport {
  /** Points handed to refiners. */
  delivered: number;
  /** Points stored in batteries. */
  charged: number;
}

/** One tick of `owner`'s power network. A board without a source pays one scan. */
export function runPower(position: Position, industry: IndustryState, owner: Color): PowerReport {
  const report: PowerReport = { delivered: 0, charged: 0 };
  const generators: Source[] = [];
  const batteries: Source[] = [];
  for (const at of structureSquares(industry)) {
    const structure = structureAt(industry, at)!;
    if (structure.owner !== owner) continue;
    if (structure.kind === COAL_GENERATOR) {
      generators.push({ at, structure, left: Math.min(POWER.coalRate, coalIn(structure)), hubs: [] });
    } else if (structure.kind === NUCLEAR_GENERATOR || structure.kind === WELL) {
      // The Sacred Well is a Nuclear Generator without the blast (§31 A4) —
      // the structure alone; its Scepter is for the seizure.
      generators.push({ at, structure, left: POWER.nuclearRate, hubs: [] });
    } else if (structure.kind === BATTERY) {
      batteries.push({ at, structure, left: structure.charge ?? 0, hubs: [] });
    }
  }
  if (generators.length === 0 && batteries.length === 0) return report;
  for (const source of [...generators, ...batteries]) source.hubs = hubsOf(industry, owner, source.at);

  const sources = [...generators, ...batteries];

  // **The design's first tier, "structures"** (§31 A9): a Shield Generator
  // whose badge is borne takes points until fed its round's
  // `DEFLECTOR.fuelPerRound`, by square, before any refiner.
  for (const at of shieldsHungry(industry, owner)) {
    const shield = structureAt(industry, at)!;
    while ((shield.fed ?? 0) < DEFLECTOR.fuelPerRound) {
      const source = sources.find((one) => one.left > 0 && hubsReach(one.hubs, at));
      if (!source) break;
      spend(source);
      shield.fed = (shield.fed ?? 0) + 1;
      report.delivered++;
    }
  }
  // Then turrets (§31 S8), the same tier: banked for the round — a Laser a
  // point a shot left, a Kinetic or Cannon one for the round — by square.
  for (const at of structureSquares(industry)) {
    const turret = structureAt(industry, at)!;
    if (turret.owner !== owner || !turret.turret) continue;
    for (let want = turretFuelWant(turret); want > 0; want--) {
      const source = sources.find((one) => one.left > 0 && hubsReach(one.hubs, at));
      if (!source) break;
      spend(source);
      turret.turret.fuel++;
      report.delivered++;
    }
  }

  // The consumers: refiners wanting a point, coal-less before coal-holding.
  const bare: number[] = [];
  const holding: number[] = [];
  for (const sq of position.cellList) {
    const piece = pieceAt(position, sq);
    if (!piece || pieceColor(piece) !== owner || !wantsFuel(position, industry, sq)) continue;
    (coalHeld(industry, sq) > 0 ? holding : bare).push(sq);
  }
  for (const sq of [...bare, ...holding]) {
    const source = sources.find((one) => one.left > 0 && hubsReach(one.hubs, sq));
    if (!source) continue;
    spend(source);
    setFuel(industry, sq, 1);
    report.delivered++;
  }

  // Last in the order: what the generators have left charges the batteries.
  for (const generator of generators) {
    for (const battery of batteries) {
      if (generator.left <= 0) break;
      if (!hubsReach(generator.hubs, battery.at) && !hubsReach(battery.hubs, generator.at)) continue;
      const room = POWER.batteryCapacity - (battery.structure.charge ?? 0);
      for (let i = 0; i < room && generator.left > 0; i++) {
        spend(generator);
        battery.structure.charge = (battery.structure.charge ?? 0) + 1;
        report.charged++;
      }
    }
  }
  return report;
}

/** `owner`'s Shield Generators with powered badges still short of the round's points, by square. */
function shieldsHungry(industry: IndustryState, owner: Color): number[] {
  return structureSquares(industry).filter((sq) => {
    const one = structureAt(industry, sq)!;
    return (
      one.kind === SHIELD_GENERATOR &&
      one.owner === owner &&
      (one.fed ?? 0) < DEFLECTOR.fuelPerRound &&
      poweredGearAt(industry, sq) !== undefined
    );
  });
}

/**
 * Close `owner`'s turn for its Shield Generators (§31 A9, `endTurn`): each is
 * **lit** for the coming round iff it was fed `DEFLECTOR.fuelPerRound` this
 * turn, and the count starts again.
 */
export function settleShields(industry: IndustryState, owner: Color): void {
  for (const sq of structureSquares(industry)) {
    const one = structureAt(industry, sq)!;
    if (one.kind !== SHIELD_GENERATOR || one.owner !== owner) continue;
    const lit = (one.fed ?? 0) >= DEFLECTOR.fuelPerRound;
    if (lit) one.lit = true;
    else delete one.lit;
    delete one.fed;
  }
}

/**
 * `owner`'s sources whose network reaches `at`, in `runPower`'s order —
 * generators, then batteries, each by square — with what each may give one
 * draw: a Coal Generator its rate or its coal, a Nuclear its rate, a Battery
 * its charge. What an upgrade at `at` may burn (§31 P3); a draw is outside
 * the tick, so the rate caps one draw, not a tick's worth.
 */
function sourcesReaching(industry: IndustryState, owner: Color, at: number): Source[] {
  const generators: Source[] = [];
  const batteries: Source[] = [];
  for (const sq of structureSquares(industry)) {
    const structure = structureAt(industry, sq)!;
    if (structure.owner !== owner) continue;
    let left = 0;
    if (structure.kind === COAL_GENERATOR) left = Math.min(POWER.coalRate, coalIn(structure));
    else if (structure.kind === NUCLEAR_GENERATOR || structure.kind === WELL) left = POWER.nuclearRate;
    else if (structure.kind === BATTERY) left = structure.charge ?? 0;
    else continue;
    const source: Source = { at: sq, structure, left, hubs: hubsOf(industry, owner, sq) };
    if (left <= 0 || !hubsReach(source.hubs, at)) continue;
    (structure.kind === BATTERY ? batteries : generators).push(source);
  }
  return [...generators, ...batteries];
}

/** Points `owner`'s network could hand a draw at `at` right now. */
export const networkFuel = (industry: IndustryState, owner: Color, at: number): number =>
  sourcesReaching(industry, owner, at).reduce((sum, source) => sum + source.left, 0);

/** Draw up to `points` from the network reaching `at`; how many it gave. */
export function drawNetworkFuel(
  industry: IndustryState,
  owner: Color,
  at: number,
  points: number
): number {
  let drawn = 0;
  for (const source of sourcesReaching(industry, owner, at)) {
    while (drawn < points && source.left > 0) {
      spend(source);
      drawn++;
    }
  }
  return drawn;
}
